import type { Catalogue } from "./catalogue";

/**
 * Polski.
 *
 * Dwie zasady, które kosztowały tu najwięcej uwagi.
 *
 * BEZ RODZAJU. "You signed in" tłumaczy się na "Zalogowałeś się" albo
 * "Zalogowałaś się", a interfejs nie wie, kto czyta. Każde takie zdanie jest
 * przeformułowane bezosobowo ("Logowanie odbyło się…"), bo wybór jednej formy
 * jest komunikatem samym w sobie - i zawsze dla kogoś błędnym.
 *
 * LICZBA MNOGA MA CZTERY PRZYPADKI, nie dwa. 1 znak, 2-4 znaki, 5+ znaków,
 * i znowu 22 znaki - `Intl.PluralRules` rozstrzyga to za nas przez sufiksy
 * `_one`/`_few`/`_many`/`_other`.
 *
 * WSZYSTKIE CZTERY MUSZĄ ISTNIEĆ, także wtedy, gdy brzmią identycznie. Brak
 * `_few` nie powoduje sięgnięcia po `_other` z tego samego katalogu — i18next
 * schodzi do języka zapasowego, więc "{{count}} min temu" bez formy `_few`
 * renderowało dla 3 minut angielskie "3 min ago". Pilnuje tego
 * `catalogue.test.ts`, który pyta o wymagane formy `Intl.PluralRules`, a nie
 * ma ich wpisanych na sztywno.
 */
const pl: Catalogue = {
  common: {
    loading: "wczytywanie…",
    save: "Zapisz",
    saving: "Zapisywanie…",
    cancel: "Anuluj",
    close: "Zamknij",
    delete: "Usuń",
    edit: "Edytuj",
    retry: "Spróbuj ponownie",
    search: "Szukaj",
    filter: "Filtr",
    all: "Wszystkie",
    none: "brak",
    unknown: "nieznane",
    yes: "tak",
    no: "nie",
    more: "Więcej",
    back: "Wstecz",
    copy: "Kopiuj",
    copied: "Skopiowano",
    dash: "—",
    crumbs: "Gdzie jest ta strona",
  },

  nav: {
    campaigns: "Kampanie",
    account: "Konto",
    now: "Teraz",
    projects: "Projekty",
    benches: "Ławki",
    runs: "Biegi",
    reports: "Raporty",
    quality: "Jakość",
    manual: "Testy ręczne",
    people: "Ludzie",
    more: "Więcej",
    menu: "Menu",
    main: "główna",
    signOut: "Wyloguj",
    readOnly: "tylko odczyt",
    readOnlyTitle: "tylko do odczytu",
    admin: "admin",
    adminTitle: "administrator instancji",
    noPassword: "brak hasła",
    notFound: "Nie ma takiej strony.",
  },

  theme: {
    label: "Schemat kolorów",
    light: "Jasny",
    auto: "Jak w systemie",
    dark: "Ciemny",
  },

  lang: {
    uncheckedFi: "Ten fiński powstał bez udziału osoby znającej język i nie był sprawdzony. Jeśli coś brzmi źle, powiedz — da się to poprawić.",
    label: "Język",
  },

  time: {
    justNow: "przed chwilą",
    minutesAgo_one: "{{count}} min temu",
    minutesAgo_few: "{{count}} min temu",
    minutesAgo_many: "{{count}} min temu",
    minutesAgo_other: "{{count}} min temu",
    hoursAgo_one: "{{count}} godz. temu",
    hoursAgo_few: "{{count}} godz. temu",
    hoursAgo_many: "{{count}} godz. temu",
    hoursAgo_other: "{{count}} godz. temu",
    daysAgo_one: "{{count}} dzień temu",
    daysAgo_few: "{{count}} dni temu",
    daysAgo_many: "{{count}} dni temu",
    daysAgo_other: "{{count}} dni temu",
    days: "niedz. pon. wt. śr. czw. pt. sob.",
    months: "sty lut mar kwi maj cze lip sie wrz paź lis gru",
    onDate: "{{day}} {{month}}, {{time}}",
    onDateWithYear: "{{day}} {{month}} {{year}}, {{time}}",
    onDay: "{{day}} {{month}} {{year}}",
    durationHours: "{{h}} godz. {{m}} min",
    durationMinutes: "{{m}} min {{s}} s",
    durationSeconds: "{{s}} s",
  },

  login: {
    title: "Logowanie",
    heading: "HIL",
    prompt: "Zaloguj się, żeby zobaczyć ławki.",
    noPassword: "Ta instalacja nie ma ustawionego hasła.",
    username: "Nazwa użytkownika",
    password: "Hasło",
    submit: "Zaloguj",
    submitting: "Logowanie…",
  },

  password: {
    intro:
      "Logowanie odbyło się hasłem tymczasowym. Zmień je, zanim przejdziesz dalej — do tego czasu nic innego nie da się zmienić.",
    title: "Zmiana hasła",
    heading: "Wybierz hasło",
    current: "Hasło tymczasowe",
    next_one: "Nowe hasło (co najmniej {{count}} znak)",
    next_few: "Nowe hasło (co najmniej {{count}} znaki)",
    next_many: "Nowe hasło (co najmniej {{count}} znaków)",
    next_other: "Nowe hasło (co najmniej {{count}} znaków)",
    tooShort_one: "jeszcze {{count}} znak",
    tooShort_few: "jeszcze {{count}} znaki",
    tooShort_many: "jeszcze {{count}} znaków",
    tooShort_other: "jeszcze {{count}} znaków",
    again: "Powtórz nowe hasło",
    same: "musi różnić się od tymczasowego",
    mismatch: "nie zgadza się",
    submit: "Ustaw hasło",
  },

  roles: {
    name: {
      guest: "gość",
      operator: "operator",
      maintainer: "opiekun",
      owner: "właściciel",
    },
    means: {
      guest: "czyta biegi, raporty i konfigurację",
      operator: "bierze dzierżawę i zleca biegi — rusza sprzętem",
      maintainer: "konfiguruje ławki, rotuje tokeny, usuwa biegi",
      owner: "decyduje, kto jeszcze ma dostęp",
    },
  },

  config: {
    help: {
      HIL_SSH: "adres SSH badanego ekranu",
      HIL_DUT_HOST: "sam adres jednostki",
      HIL_API_BASE: "korzeń API AO2025",
      HIL_KVM: "adres JetKVM",
      HIL_MODEM_HOST: "interfejs zarządzania modemem",
      HIL_SIVMOCK: "makieta SIV używana przez tę ławkę",
      HIL_CAPS: "zadeklarowane możliwości",
    },
  },

  scope: {
    notRecorded: "które testy: nienotowane",
    notRecordedTitle:
      "zapisane, zanim notowano wybór testów — mógł to być cały zestaw albo wycinek",
    slice: "część testów",
    sliceTitle: "część testów, nie wszystkie",
    matching: "tylko testy pasujące do „{{filter}}”",
    onlyFile: "tylko {{file}}",
    marker: "znacznik: {{marker}}",
    files: "pliki: {{files}}",
  },

  build: {
    notRecorded: "wersja nienotowana",
    notRecordedTitle: "ten bieg jest starszy niż raportowanie wersji przez harness",
  },

  verdict: {
    running: "trwa",
    noVerdicts: "brak werdyktów",
    nothingRecorded: "nic nie zapisano",
    clean: "czysto",
    failed_one: "{{count}} niepowodzenie",
    failed_few: "{{count}} niepowodzenia",
    failed_many: "{{count}} niepowodzeń",
    failed_other: "{{count}} niepowodzeń",
    passed_one: "{{count}} zaliczony",
    passed_few: "{{count}} zaliczone",
    passed_many: "{{count}} zaliczonych",
    passed_other: "{{count}} zaliczonych",
    naHere_one: "{{count}} nieuruchomiony tutaj",
    naHere_few: "{{count}} nieuruchomione tutaj",
    naHere_many: "{{count}} nieuruchomionych tutaj",
    naHere_other: "{{count}} nieuruchomionego tutaj",
    xfailed_one: "{{count}} oczekiwane niepowodzenie",
    xfailed_few: "{{count}} oczekiwane niepowodzenia",
    xfailed_many: "{{count}} oczekiwanych niepowodzeń",
    xfailed_other: "{{count}} oczekiwanego niepowodzenia",
    barLabel:
      "{{failed}} niepowodzeń, {{passed}} zaliczonych, {{skipped}} nieuruchomionych tutaj, {{xfailed}} oczekiwanych niepowodzeń",
    legend: {
      failed: "niepowodzenia",
      xfailed: "oczekiwane niepowodzenie",
      skipped: "nieuruchomione tutaj",
      passed: "zaliczone",
    },
  },

  run: {
    steps_one: "{{count}} krok",
    steps_few: "{{count}} kroki",
    steps_many: "{{count}} kroków",
    steps_other: "{{count}} kroków",
  },

  caps: {
    heading: "Możliwości",
    none:
      "Żaden bieg jeszcze nic nie zgłosił, więc nie ma czego pokazać. Możliwości ławki ustala się przy starcie biegu, a nie konfiguruje.",
    noDescription: "dla tej możliwości nie podano opisu",
    lastRun: "ostatni bieg",
    reportedBy_one:
      "{{count}} zgłoszona przez <runLink>{{run}}</runLink> · ustalone przez odpytanie ławki przy starcie, nie odczytane z konfiguracji",
    reportedBy_few:
      "{{count}} zgłoszone przez <runLink>{{run}}</runLink> · ustalone przez odpytanie ławki przy starcie, nie odczytane z konfiguracji",
    reportedBy_many:
      "{{count}} zgłoszonych przez <runLink>{{run}}</runLink> · ustalone przez odpytanie ławki przy starcie, nie odczytane z konfiguracji",
    reportedBy_other:
      "{{count}} zgłoszonych przez <runLink>{{run}}</runLink> · ustalone przez odpytanie ławki przy starcie, nie odczytane z konfiguracji",
  },

  live: {
    onBench: "na <benchLink>{{bench}}</benchLink>",
    step: "krok {{no}}",
    stepOf: "krok {{no}} z {{total}}",
    unknownStep: "?",
    starting: "start — autotesty harnessu",
    elapsedSeconds: "{{s}} s",
    elapsedMinutes: "{{m}} min",
    elapsedHours: "{{h}} godz.",
  },

  configPanel: {
    heading: "Konfiguracja",
    edit: "Edytuj konfigurację",
    nothingSet:
      "Nic nie ustawiono — ławka używa wartości domyślnych wbudowanych w <code>run.sh</code>.",
    note:
      "Czytane przy starcie każdego biegu. Wartość podana w wierszu poleceń nadal wygrywa, a ławka bez wpisu tutaj bierze domyślne z <code>run.sh</code>. <b>Żadnych haseł ani kluczy API</b> — te zostają na ławce. Każdy, kto może czytać tę ławkę, przeczyta tu każdą wartość.",
    capsNone: "nic nie zadeklarowano",
    capsAdd: "dopisz możliwość i naciśnij Enter",
    capsRemove: "usuń {{cap}}",
  },

  members: {
    heading: "Członkowie ({{count}})",
    add: "Dodaj członka",
    needOwner: "do zmian potrzebna jest tu rola właściciela",
    emptyBench:
      "Nikomu nie nadano uprawnień na samej tej ławce — dostęp idzie przez projekt.",
    emptyProject: "Jeszcze nikogo. Nadaj tu rolę osobie albo grupie.",
    roleFor: "rola dla {{name}}",
    remove: "usuń",
    inherited: "Odziedziczone z projektu ({{count}})",
    inheritedNote:
      "Te zmienia się w projekcie. Nadanie zrobione tutaj może tylko dodać.",
    kind: { user: "Osoba", group: "Grupa" },
    kindPersonTitle: "jedna osoba",
    kindGroupTitle: "grupa osób",
    deactivated: "nieaktywne",
    roleKeySummary: "co znaczą te role",
    grantTo: "Nadaj",
    subjectKind: "rodzaj podmiotu",
    aPerson: "osobie",
    aGroup: "grupie",
    person: "Osoba",
    group: "Grupa",
    subject: "podmiot",
    choose: "wybierz…",
    role: "Rola",
    addButton: "Dodaj",
  },

  auto: {
    title: "Testy automatyczne",
    stalled: "Ławka nie odzywa się od {{when}}, więc od tamtej pory nic nie ruszyło samo.",
    sameProduct: "{{build}} pojawił się {{when}} z tym samym buildem produktu ({{product}}) co już przetestowany, więc nie uruchomił kampanii.",
    off: "Ta ławka niczego nie testuje sama.",
    summary: "Ta ławka uruchamia {{what}}.",
    onNewBuild: "kampanię dla każdej nowej wersji",
    nightlyAt: "kampanię każdej nocy o {{time}}",
    and: " oraz ",
    newBuildLabel: "Uruchamiaj kampanię dla każdej nowej wersji, która pojawi się na tej ławce",
    nightlyLabel: "Uruchamiaj kampanię każdej nocy o",
    nightlyTime: "Godzina nocnej kampanii",
    localTime: "twój czas lokalny",
    cost: "Każda kampania zajmuje ławkę, dopóki nie wykona się każdy profil. Nigdy nie rusza, gdy czyjaś praca czeka w kolejce albo trwa albo gdy ławka jest zajęta; wtedy czeka.",
    save: "Zapisz",
    saved: "Zapisano.",
    waiting: "Czeka na uruchomienie: {{what}} ({{build}}) — {{why}}.",
    last: "Ostatnio uruchomione samo: <campaignLink>{{what}} ({{build}})</campaignLink>, {{when}}.",
    missed: "Nocna kampania z {{day}} się nie odbyła: {{why}}.",
    installed: "wersja zainstalowana",
    origin: {
      new_build: "kampania dla nowej wersji",
      nightly: "kampania nocna",
      manual: "kampania",
    },
  },
  likeness: {
    flat: "Ten sam wynik w każdym przebiegu: stała różnica, nie migotanie.",
    title: "Zgodność z {{golden}}, bieg po biegu",
    aria: "Zgodność z {{golden}} w {{count}} biegach, z progiem",
    threshold: "próg {{value}}",
    point: "{{run}}: {{score}} (próg {{threshold}})",
    table: "Liczby",
    passed: "przeszło",
    missed: "poniżej progu",
    near: "Przeszło tuż nad progiem: {{score}} przy progu {{threshold}}, zapas {{gap}}.",
  },
  product: {
    choose: "Wybierz produkt",
    panel: "Produkty",
    all: "Wszystkie produkty",
    list: "Lista projektów",
    hint: "Każda karta mówi, jaki jest najnowszy wynik produktu.",
    tabs: "W tym produkcie",
    tabOverview: "Przegląd",
    tabBuilds: "Buildy",
    tabTests: "Testy",
    tabBenches: "Ławki",
    tabSettings: "Ustawienia",
    search: "Szukaj",
    searchPlaceholder: "Produkt, ławka, strona albo bieg",
    noHits: "Nic nie pasuje.",
    kind: { product: "produkt", page: "strona", bench: "ławka", run: "bieg", build: "build", case: "przypadek" },
    noBenches: "jeszcze bez ławki",
    online: "ławki online: {{online}} z {{total}}",
    offlineFor_one: "ławka offline od {{count}} dnia",
    offlineFor_few: "ławka offline od {{count}} dni",
    offlineFor_many: "ławka offline od {{count}} dni",
    offlineFor_other: "ławka offline od {{count}} dnia",
    offline: "ławka offline",
    installed: "zainstalowana wersja",
    campRunning: "{{build}}: trwa kampania",
    campGreen: "{{build}}: wszystko zaplanowane przeszło",
    campKnown: "{{build}}: nic nowego nie zawiodło; znane problemy: {{n}}",
    campFailed_one: "{{build}}: {{count}} test zawiódł",
    campFailed_few: "{{build}}: {{count}} testy zawiodły",
    campFailed_many: "{{build}}: {{count}} testów zawiodło",
    campFailed_other: "{{build}}: {{count}} testu zawiodło",
    campLost: "{{build}}: bieg nie przysłał wyników",
    campShort: "{{build}}: nie wszystko zaplanowane się wykonało",
    runFailed_one: "{{run}}: {{count}} test zawiódł",
    runFailed_few: "{{run}}: {{count}} testy zawiodły",
    runFailed_many: "{{run}}: {{count}} testów zawiodło",
    runFailed_other: "{{run}}: {{count}} testu zawiodło",
    runClean: "{{run}}: nic nie zawiodło",
    nothingYet: "jeszcze nic nie biegło",
    empty: "Jeszcze bez ławki i przebiegów:",
  },
  builds: {
    listTitle: "Buildy",
    title: "Build {{build}}",
    back: "buildy",
    listAnswer_one: "{{count}} build, najnowszy {{build}}.",
    listAnswer_few: "{{count}} buildy, najnowszy {{build}}.",
    listAnswer_many: "{{count}} buildów, najnowszy {{build}}.",
    listAnswer_other: "{{count}} buildu, najnowszy {{build}}.",
    listAfter: "Build to wersja produktu pod testem, nie obraz ławki. Otwórz, żeby zobaczyć jego kampanie i pozostałe biegi.",
    listNone: "Żaden bieg tego produktu nie zapisał, jaki build testował.",
    listNoneAfter: "Biegi są niżej. Gdy każdy bieg zacznie zapisywać build (krok K0 dla tego produktu), zgrupują się tu w buildy.",
    counts: "biegi: {{runs}}, kampanie: {{campaigns}}",
    unbuiltTitle_one: "{{count}} bieg bez zapisanego buildu",
    unbuiltTitle_few: "{{count}} biegi bez zapisanego buildu",
    unbuiltTitle_many: "{{count}} biegów bez zapisanego buildu",
    unbuiltTitle_other: "{{count}} biegu bez zapisanego buildu",
    unbuiltNote: "Od najnowszych; pokazanych jest 20.",
    notFound: "Nie ma buildu {{build}} w tym produkcie.",
    span: "biegi: {{runs}}, {{first}} – {{last}}",
    newestCampaign: "Najnowsza kampania, #{{id}}",
    openCampaign: "Otwórz kampanię",
    campaigns_one: "{{count}} kampania",
    campaigns_few: "{{count}} kampanie",
    campaigns_many: "{{count}} kampanii",
    campaigns_other: "{{count}} kampanii",
    campaignName: "Kampania #{{id}}",
    ranOf: "wykonano {{ran}} z {{planned}}",
    fullRuns_one: "{{count}} pełny bieg poza kampanią",
    fullRuns_few: "{{count}} pełne biegi poza kampanią",
    fullRuns_many: "{{count}} pełnych biegów poza kampanią",
    fullRuns_other: "{{count}} pełnego biegu poza kampanią",
    slices_one: "{{count}} bieg na części testów",
    slices_few: "{{count}} biegi na części testów",
    slices_many: "{{count}} biegów na części testów",
    slices_other: "{{count}} biegu na części testów",
    slicesNote: "Weryfikacje poprawek, nagrania, próby. Każdy mówi tylko o swoich przypadkach.",
    route: "Przystanki kampanii, po kolei",
    stopLost: "wyniki nie dotarły",
    stopKnown: "znane {{n}}",
    stopFailed: "zawiodło {{n}}",
    wRunning: "trwa",
    wGreen: "zielony",
    wKnown: "znane problemy",
    wFailed_one: "{{count}} zawiódł",
    wFailed_few: "{{count}} zawiodły",
    wFailed_many: "{{count}} zawiodło",
    wFailed_other: "{{count}} zawiodło",
    wLost: "bez wyników",
    wShort: "nie wszystko",
    wNone: "nic nie biegło",
    wClean: "nic nie zawiodło",
    wNoResults: "bez wyników",
    aRunning: "Trwa kampania.",
    aGreen: "Wszystko zaplanowane się wykonało i nic nie zawiodło.",
    aKnown: "Nic nowego nie zawiodło. Znane problemy: {{n}}.",
    aFailed_one: "W najnowszej kampanii {{count}} test zawiódł.",
    aFailed_few: "W najnowszej kampanii {{count}} testy zawiodły.",
    aFailed_many: "W najnowszej kampanii {{count}} testów zawiodło.",
    aFailed_other: "W najnowszej kampanii {{count}} testu zawiodło.",
    aLost: "Bieg z najnowszej kampanii nie przysłał wyników.",
    aShort: "W najnowszej kampanii nie wykonało się wszystko, co zaplanowano.",
    aNone: "Na tym buildzie nic jeszcze nie biegło.",
    aRunFailed_one: "{{run}}: {{count}} test zawiódł.",
    aRunFailed_few: "{{run}}: {{count}} testy zawiodły.",
    aRunFailed_many: "{{run}}: {{count}} testów zawiodło.",
    aRunFailed_other: "{{run}}: {{count}} testu zawiodło.",
    aRunClean: "{{run}}: nic nie zawiodło.",
    startCampaign: "Uruchom kampanię",
    report: "Zrób raport",
    images_one: "Obraz ławki ({{count}})",
    images_few: "Obrazy ławki ({{count}})",
    images_many: "Obrazy ławki ({{count}})",
    images_other: "Obrazy ławki ({{count}})",
    telling: "Przypadki, które coś mówią",
    tellingNote: "w kampanii #{{id}}",
    tellingNone: "Nic tam nie zawiodło.",
    tellKnown: "znany",
    tellFailed: "porażka",
    open: "Otwórz",
  },
  overview: {
    leadCampaign: "Kampania #{{id}}, {{when}}: wykonano {{ran}} z {{planned}}.",
    leadBuildRuns: "Biegi tego buildu: {{runs}}, najnowszy {{when}}.",
    leadRun: "Najnowszy pełny bieg, {{when}}, na {{bench}}",
    noBuild: "Biegi tego produktu nie zapisują jeszcze, jaki build testowały (krok K0), więc to jego najnowszy pełny bieg, a nie build.",
    nothing: "Na tym produkcie nic jeszcze nie biegło.",
    start: "Uruchom testy",
    launch: "Zbuduj nową wersję",
    openBuild: "Otwórz build {{build}}",
    newestCampaign: "Najnowsza kampania {{build}}",
    openCampaign: "Otwórz kampanię",
    attention: "Wymaga uwagi",
    attentionNone: "Nic nie wymaga uwagi.",
    aLost: "bez wyników",
    aLostText: "{{run}} ({{stop}}) nie przysłał wyników",
    aQuiet: "cicho",
    aQuietText: "{{bench}} nie odzywa się od {{when}}",
    open: "Otwórz",
    bench: "Ławka",
    benches_one: "{{count}} ławka",
    benches_few: "{{count}} ławki",
    benches_many: "{{count}} ławek",
    benches_other: "{{count}} ławki",
    allBenches: "Ławki",
    heldBy: "zajęta przez {{actor}} {{purpose}}",
    settings: "Ustawienia: czym jest produkt, kto nad nim pracuje, co oferuje formularz uruchamiania",
  },
  productSettings: {
    back: "{{name}}",
    answer: "Czym jest ten produkt, kto nad nim pracuje i co oferuje jego formularz uruchamiania.",
  },
  caseCtx: {
    coverageView: "Pokrycie",
    manualView: "Ręcznie",
    quality: "Pokrycie grupy {{group}}",
    stale: "Ocena jest nieaktualna: przypadki grupy zmieniły się od czasu, gdy ktoś ją ocenił.",
    manual: "Ręcznie: około {{minutes}} min, priorytet {{priority}}",
    pre: "Przed: {{text}}",
    expected: "Oczekiwane: {{text}}",
  },
  benchAdmin: {
    title: "Administracja",
    note: "Zmień nazwę ławki, daj jej agentowi nowy token albo ją usuń.",
  },
  casePage: {
    crumb: "{{product}} › Testy",
    back: "testy",
    answerKnown: "Znany problem do {{date}}.",
    answerNone: "Jeszcze nie biegł.",
    answerPassed: "Przeszedł w {{run}}.",
    answerFailed: "Zawiódł w {{run}}.",
    answerOther: "{{run}}: {{verdict}}.",
    expects: "Czego oczekuje, jak porównano w {{run}}",
    mask: "maska {{areas}} obszarów, {{pct}}% ekranu",
    likeness: "Zgodność ze wzorcem, bieg po biegu",
    does: "Co robi",
    group: "Grupa",
    steps: "Kroki",
    requires: "Wymaga",
    coverage: "Pokrycie i ręcznie",
    grade: "Ocena grupy",
    byHand: "Ręcznie",
    byHandValue: "około {{minutes}} min, priorytet {{priority}}",
    expect: "Oczekiwane",
    noCoverage: "Brak oceny i procedury ręcznej dla tego przypadku.",
    history: "Wyniki, od najnowszych",
    definition: "Definicja, jak napisana",
  },
  buildPicker: {
    title: "Build DisplayOS i testy",
    explain: "Każdy obraz na ławce zawiera jeden build DisplayOS i testy, które na nim biegną. Ławka przełącza się na wybrany obraz przed biegiem; jeśli się nie da, nic nie rusza, a zlecenie podaje powód.",
    nowIs: "Teraz na ławce: {{build}}, testy {{tests}}.",
    nowBy: "Przełączyła ją",
    nowByRun: "Przełączył ją {{run}}, {{when}}.",
    nowCaveat: "Przełączenie zrobione ręcznie na ławce nie jest tu widoczne.",
    nowUnknown: "Nie wiadomo tu, na jakim obrazie jest ławka: żaden bieg z hil-central jeszcze jej nie przełączył.",
    keep: "Zostaw to, co jest na ławce",
    keepNow: "Bez przełączania: biegnie {{build}} z testami {{tests}}, jak teraz.",
    keepWhy: "Bez przełączania. Biegnie na obrazie, który ławka ma teraz.",
    tests: "Testy {{sha}}",
    scripts: "skrypty ławki {{sha}}",
    pointer: "{{tag}}",
    pointerWhy: "inna nazwa jednego z obrazów powyżej; którego, tu nie wiadomo",
    oldShape: "oznaczony po staremu: nazwa nie mówi, jakie ma testy",
    newest: "najnowszy · używa go kampania nocna",
    onBenchNow: "teraz na ławce",
    olderTests: "starsze testy",
    download: "do pobrania · minuty",
    lastUsed: "ostatnio {{when}}, {{what}}",
    neverUsed: "jeszcze nie używany z hil-central",
    campaign: "kampania #{{id}}",
    olderWarning: "Ten obraz ma starsze testy niż najnowszy dla tego samego buildu DisplayOS. Przypadki i dane testowe mogą się różnić od dzisiejszych, więc różnica w wynikach może pochodzić z testów, a nie z DisplayOS.",
    registry_one: "{{count}} obraz w rejestrze (do pobrania)",
    registry_few: "{{count}} obrazy w rejestrze (do pobrania)",
    registry_many: "{{count}} obrazów w rejestrze (do pobrania)",
    registry_other: "{{count}} obrazu w rejestrze (do pobrania)",
    other_one: "{{count}} inna nazwa na ławce",
    other_few: "{{count}} inne nazwy na ławce",
    other_many: "{{count}} innych nazw na ławce",
    other_other: "{{count}} innej nazwy na ławce",
    typeIt: "Wpisz tag obrazu",
  },
  runAbout: {
    line_one: "Sprawdza {{count}} grupę przypadków: {{names}}.",
    line_few: "Sprawdza {{count}} grupy przypadków: {{names}}.",
    line_many: "Sprawdza {{count}} grup przypadków: {{names}}.",
    line_other: "Sprawdza {{count}} grupy przypadków: {{names}}.",
    lineMore_one: "Sprawdza {{count}} grupę przypadków: {{names}} i {{more}} innych.",
    lineMore_few: "Sprawdza {{count}} grupy przypadków: {{names}} i {{more}} innych.",
    lineMore_many: "Sprawdza {{count}} grup przypadków: {{names}} i {{more}} innych.",
    lineMore_other: "Sprawdza {{count}} grupy przypadków: {{names}} i {{more}} innych.",
    howReplay: "Odtwarza nagrany ruch OBA, na layoucie {{layout}}.",
    howSimulated: "Steruje layoutem {{layout}} symulowanym przejazdem z OBA Emulatora.",
    howFixture: "Steruje własnym layoutem testowym harnessu (hil_fixture) symulowanym przejazdem z OBA Emulatora.",
    howOther: "Tryb danych {{mode}}, na layoucie {{layout}}.",
    theLayout: "klienta",
    checks: "Co sprawdza:",
    cases_one: "{{count}} przypadek",
    cases_few: "{{count}} przypadki",
    cases_many: "{{count}} przypadków",
    cases_other: "{{count}} przypadku",
    failed_one: "{{count}} porażka",
    failed_few: "{{count}} porażki",
    failed_many: "{{count}} porażek",
    failed_other: "{{count}} porażki",
    more_one: "jeszcze {{count}} grupa",
    more_few: "jeszcze {{count}} grupy",
    more_many: "jeszcze {{count}} grup",
    more_other: "jeszcze {{count}} grupy",
    fewer: "mniej",
  },
  known: {
    status: "znany",
    short: "Znany problem do {{date}}.",
    shared_one: "{{count}} znany problem: {{reason}}",
    shared_few: "{{count}} znane problemy, wszystkie z jednego powodu: {{reason}}",
    shared_many: "{{count}} znanych problemów, wszystkie z jednego powodu: {{reason}}",
    shared_other: "{{count}} znanego problemu, wszystkie z jednego powodu: {{reason}}",
    title: "Znane problemy",
    intro: "Przypadki, których porażkę ktoś wyjaśnił, do określonej daty. Na swoich biegach nadal są porażkami; kampanie i Now liczą je osobno, żeby nowa porażka była widoczna.",
    none: "Brak znanych problemów.",
    onlyKnown: "Nic nowego nie zawiodło. Znane problemy: {{n}}.",
    onlyKnownNewest: "W najnowszej kampanii nic nowego nie zawiodło. Znane problemy: {{n}}.",
    also: "Znane problemy: {{n}}.",
    word: "znane problemy",
    onStop: "Znane problemy tutaj: {{list}}.",
    chip: "Znany problem do {{date}}: {{reason}}",
    by: "ustawił(a) {{who}}",
    mark: "Oznacz jako znany problem",
    reason: "Dlaczego zawodzi",
    until: "Do",
    save: "Zapisz",
    cancel: "Anuluj",
    end: "Zakończ",
    stateOpen: "do {{date}}",
    stateExpired: "wygasł {{date}}",
    closedPassed: "zakończony {{date}}: przypadek przeszedł",
    closedRemoved: "zakończony {{date}} przez {{who}}",
  },
  campaigns: {
    title: "Kampanie",
    heading: "Kampanie",
    answerNone: "Nie było tu jeszcze żadnej kampanii.",
    answerGreen: "Najnowsza kampania wykonała wszystko, co zaplanowano, i nic nie zawiodło.",
    answerRunning: "Na ławce {{bench}} trwa teraz kampania.",
    answerCancelled: "Najnowszą kampanię anulowano, zanim wszystko się wykonało.",
    answerFailed_one: "W najnowszej kampanii {{count}} test zawiódł.",
    answerFailed_few: "W najnowszej kampanii {{count}} testy zawiodły.",
    answerFailed_many: "W najnowszej kampanii {{count}} testów zawiodło.",
    answerFailed_other: "W najnowszej kampanii {{count}} testu zawiodło.",
    answerNotAllRan: "Najnowsza kampania się wykonała, ale nie wszystko, co zaplanowano.",
    answerLost: "Bieg z najnowszej kampanii stanął, zanim testy zgłosiły wyniki.",
    loaded_one: "{{count}} kampania, od najnowszej.",
    loaded_few: "{{count}} kampanie, od najnowszej.",
    loaded_many: "{{count}} kampanii, od najnowszej.",
    loaded_other: "{{count}} kampanii, od najnowszej.",
    projectViews: "Który projekt",
    outcomeViews: "Jaki wynik",
    allProjects: "Wszystkie projekty",
    any: "Każdy wynik",
    onlyNotGreen: "Niezielone",
    onlyGreen: "Zielone",
    onlyRunning: "Trwające",
    oneBench: "Tylko kampanie ławki {{bench}}.",
    showEveryBench: "Pokaż wszystkie ławki",
    empty: "Nie było tu jeszcze żadnej kampanii. Kampania wykonuje po kolei każdy profil ławki.",
    noneMatch: "Żadna kampania tutaj tego nie spełnia.",
    buildGroup_one: "Wersja {{build}} · {{count}} kampania",
    buildGroup_few: "Wersja {{build}} · {{count}} kampanie",
    buildGroup_many: "Wersja {{build}} · {{count}} kampanii",
    buildGroup_other: "Wersja {{build}} · {{count}} kampanii",
    installedGroup_one: "To, co było zainstalowane na ławce {{bench}} · {{count}} kampania",
    installedGroup_few: "To, co było zainstalowane na ławce {{bench}} · {{count}} kampanie",
    installedGroup_many: "To, co było zainstalowane na ławce {{bench}} · {{count}} kampanii",
    installedGroup_other: "To, co było zainstalowane na ławce {{bench}} · {{count}} kampanii",
    rowTitle: "Kampania #{{id}} na ławce {{bench}}",
    loadMore: "Pokaż kolejne {{count}}",
    filterNote_one: "Filtrowane na tej stronie, spośród {{count}} wczytanej kampanii.",
    filterNote_few: "Filtrowane na tej stronie, spośród {{count}} wczytanych kampanii.",
    filterNote_many: "Filtrowane na tej stronie, spośród {{count}} wczytanych kampanii.",
    filterNote_other: "Filtrowane na tej stronie, spośród {{count}} wczytanych kampanii.",
    origin: {
      manual: "uruchomił {{who}}",
      new_build: "uruchomiona sama, nowa wersja",
      nightly: "uruchomiona sama, w nocy",
    },
  },
  campaign: {
    badge: "Kampania #{{id}}",
    stops_one: "{{count}} przystanek",
    stops_few: "{{count}} przystanki",
    stops_many: "{{count}} przystanków",
    stops_other: "{{count}} przystanków",
    routeHeading: "Biegi",
    why: "Po co: {{reason}}",
    notFound: "Nie ma kampanii #{{id}}. Strona kampanii wymienia wszystkie.",
    report: "Zrób raport z tej kampanii",
    origin: {
      new_build: "uruchomiona sama, gdy pojawiła się nowa wersja, {{when}}",
      nightly: "uruchomiona sama w nocy, {{when}}",
    },
    loading: "Wczytywanie kampanii…",
    back: "← {{bench}}",
    title: "Kampania na ławce {{bench}}",
    build: "wersja {{build}}",
    installed: "wersja już zainstalowana",
    startedBy: "uruchomiona przez {{who}} {{when}}",
    answerGreen: "Wszystko, co zaplanowano, się wykonało i nic nie zawiodło.",
    answerRunning: "Trwa bieg {{n}} z {{of}}: {{label}}.",
    answerWaiting: "Czekamy, aż ławka uruchomi kolejny bieg.",
    answerCancelled: "Anulowano, zanim wszystko się wykonało.",
    answerFailed_one: "{{count}} test zawiódł.",
    answerFailed_few: "{{count}} testy zawiodły.",
    answerFailed_many: "{{count}} testów zawiodło.",
    answerFailed_other: "{{count}} testu zawiodło.",
    answerNotAllRan: "Nic nie zawiodło, ale nie wszystko, co zaplanowano, się wykonało.",
    answerLost: "Bieg stanął, zanim testy zgłosiły wyniki, więc część tej kampanii nie ma wyniku.",
    stopLost: "Ten bieg stanął, zanim testy zgłosiły wyniki. Przypadki z nieudanym sprawdzeniem: {{n}}.",
    imageDetails: "Obraz ławki",
    planned_one: "zaplanowano {{count}}",
    planned_few: "zaplanowano {{count}}",
    planned_many: "zaplanowano {{count}}",
    planned_other: "zaplanowano {{count}}",
    ran_one: "wykonano {{count}}",
    ran_few: "wykonano {{count}}",
    ran_many: "wykonano {{count}}",
    ran_other: "wykonano {{count}}",
    passed_one: "{{count}} zaliczony",
    passed_few: "{{count}} zaliczone",
    passed_many: "{{count}} zaliczonych",
    passed_other: "{{count}} zaliczonego",
    expected_one: "{{count}} oczekiwane niepowodzenie",
    expected_few: "{{count}} oczekiwane niepowodzenia",
    expected_many: "{{count}} oczekiwanych niepowodzeń",
    expected_other: "{{count}} oczekiwanego niepowodzenia",
    failed_one: "{{count}} niepowodzenie",
    failed_few: "{{count}} niepowodzenia",
    failed_many: "{{count}} niepowodzeń",
    failed_other: "{{count}} niepowodzenia",
    notRun_one: "{{count}} nieuruchomiony",
    notRun_few: "{{count}} nieuruchomione",
    notRun_many: "{{count}} nieuruchomionych",
    notRun_other: "{{count}} nieuruchomionego",
    routeLabel: "Biegi tej kampanii, po kolei",
    attempt: "próba {{n}}",
    openRun: "Otwórz bieg",
    retry: "Uruchom ponownie",
    retried: "Ponownie w kolejce. Etap pokazuje nową próbę.",
    cancel: "Anuluj kampanię",
    cancelled_one: "Anulowano {{count}} bieg.",
    cancelled_few: "Anulowano {{count}} biegi.",
    cancelled_many: "Anulowano {{count}} biegów.",
    cancelled_other: "Anulowano {{count}} biegu.",
    recent: "Ostatnie kampanie na tej ławce",
    listItem: "Kampania {{when}}: wykonano {{ran}} z {{planned}}",
    state: {
      queued: "czeka",
      claimed: "startuje",
      running: "trwa",
      done: "zakończony",
      failed: "nie dokończył",
      cancelled: "anulowany",
      missing: "nie w kolejce",
    },
    status: {
      running: "trwa",
      green: "wszystko się wykonało, nic nie zawiodło",
      not_green: "nie wszystko zielone",
      cancelled: "anulowana",
    },
  },
  start: {
    everyProfile_one: "Każdy profil: {{count}} bieg, przypadków: {{cases}}",
    everyProfile_few: "Każdy profil: {{count}} biegi, przypadków: {{cases}}",
    everyProfile_many: "Każdy profil: {{count}} biegów, przypadków: {{cases}}",
    everyProfile_other: "Każdy profil: {{count}} biegu, przypadków: {{cases}}",
    everyProfileCost: "Zajmuje ławkę, dopóki każdy profil się nie wykona, jeden po drugim.",
    campaignStarted: "Kampania ruszyła. <campaignLink>Śledź ją jako trasę jej biegów.</campaignLink>",
    title: "Rozpocznij testy",
    bench: "Ławka",
    benchOffline: "{{bench}} — nie zgłasza się",
    benchHeld: "{{bench}} — trzyma {{actor}}",
    benchReadOnly: "{{bench}} — możesz tylko oglądać",
    why: "Po co",
    queued: "Dodano do kolejki na <benchLink>{{bench}}</benchLink>. Strona ławki pokazuje, jak czeka i biegnie.",
    none: "Nie ma jeszcze ławki do testów.",
    loading: "Wczytywanie ławek…",
  },
  runPanel: {
    profile: "Co testować",
    profileCases_one: "{{count}} przypadek",
    profileCases_few: "{{count}} przypadki",
    profileCases_many: "{{count}} przypadków",
    profileCases_other: "{{count}} przypadku",
    moreOptions: "Zaawansowane: wybierz testy samodzielnie",
    heading: "Rozpocznij testy",
    markers: {
      auto: "auto — w pełni automatyczne",
      screenshot: "screenshot_partial — kontrole wzrokowe",
      both: "auto + screenshot_partial",
    },
    running: "Trwa",
    queued: "W kolejce",
    askedBy: "zlecone przez {{who}} · {{when}}",
    stop: "Zatrzymaj",
    stopTitle: "zatrzymanie biegu wymaga tu roli operatora",
    stopping:
      "Poproszono ławkę o zatrzymanie. Zakończy zestaw przy najbliższym zgłoszeniu, w ciągu minuty.",
    removed: "Usunięto z kolejki.",
    cancelNote:
      "Anulowanie zapisuje decyzję; nie sięga do ławki i nie zatrzymuje zestawu.",
    waiting: "W kolejce ({{count}})",
    remove: "usuń",
    leaseHeld:
      "<b>{{actor}}</b> trzyma tę ławkę. Bieg nie ruszy, dopóki dzierżawa nie wygaśnie — ławka i tak by go odrzuciła.",
    leaseHeldFor:
      "<b>{{actor}}</b> trzyma tę ławkę: {{purpose}}. Bieg nie ruszy, dopóki dzierżawa nie wygaśnie — ławka i tak by go odrzuciła.",
    tests: "Grupa testów",
    jobMarked: "testy oznaczone „{{marker}}”",
    jobMatching: "pasujące do „{{filter}}”",
    jobBuild: "wersja {{build}}",
    onlyMatching: "Tylko pasujące",
    filterPlaceholder: "np. LED (opcjonalnie)",
    reasonPlaceholder:
      "Po co — przeczyta to następna osoba, tak jak cel dzierżawy",
    queueing: "Uruchamianie…",
    start: "Rozpocznij testy",
    needOperator:
      "Zlecenie biegu rusza sprzętem; do tego potrzebna jest rola operatora na tej ławce.",
    takesDut:
      "Zajmuje ławkę na czas testów — przy pełnym zestawie to godziny.",
    installedVersion:
      "Testuje wersję, która jest już zainstalowana na tej ławce. Wersję, której jeszcze nie zbudowano, zbudujesz najpierw w <projectLink>projekcie</projectLink>.",
    installedVersionNoProject:
      "Testuje wersję, która jest już zainstalowana na tej ławce. Ta ławka nie należy do żadnego projektu, więc nie ma skąd zbudować nowej wersji.",
    buildRef: "Wersja do wgrania najpierw (opcjonalnie)",
    buildRefPlaceholder: "puste: testuje wersję już zainstalowaną na ławce",
    buildRefList: "Już zbudowane: wybrana wersja zostanie zainstalowana, bez budowania.",
    buildRefNote: "To, o co proszono; bieg zapisuje, co faktycznie się zainstalowało. Jeśli nie da się wgrać, zestaw testów w ogóle nie rusza, a zlecenie kończy się błędem z powodem.",
    offline: "Agent ławki się nie zgłasza; nikt tego nie podejmie.",
    earlier: "wcześniej zamawiane biegi ({{count}})",
    jobState: {
      queued: "w kolejce",
      claimed: "podjęty",
      running: "trwa",
      done: "gotowe",
      failed: "niepowodzenie",
      cancelled: "anulowany",
    },
  },

  markdown: {
    empty: "Jeszcze nic tu nie napisano.",
    showAll: "Pokaż całość",
    showLess: "Pokaż mniej",
    label: "Markdown",
    tabWrite: "pisz",
    tabSplit: "obok siebie",
    tabPreview: "podgląd",
    placeholder: "# Nagłówek\n\nAdresy, osobliwości, do czego to jest podpięte…",
    previewEmpty: "Nie ma jeszcze czego podejrzeć.",
    dirty: "niezapisane zmiany",
    saved: "zapisano",
    shortcuts: "⌘/Ctrl+Enter zapisuje · Esc anuluje",
  },

  history: {
    heading: "Historia",
    scopeAll: "Wszystkie biegi",
    scopeFull: "Cały zestaw",
    scopePartial: "Wycinki",
    chart: "wykres",
    table: "tabela",
    unknownTitle:
      "zapisane, zanim run.sh notował wybór, więc nie należą do żadnej z grup",
    unknownCount: "· {{count}} o nieznanym zakresie",
    tooFewFull:
      "Mniej niż dwa biegi całego zestawu do porównania. Wybierz inny filtr, żeby zobaczyć resztę.",
    tooFewPartial:
      "Mniej niż dwa biegi częściowe do porównania. Wybierz inny filtr, żeby zobaczyć resztę.",
    colRun: "Bieg",
    colFailed: "Niepowodzenia",
    colXfailed: "Oczekiwane niepowodzenia",
    colSkipped: "Pominięte",
    colPassed: "Zaliczone",
    colCases: "Przypadki",
    chartLabel: "przypadki wg wyniku dla każdego biegu",
    trackTitle: "{{name}} — {{count}} przypadków",
    segTitle: "{{name}} — {{count}} {{outcome}} · {{pct}}% z {{total}}",
    axisRun: "bieg",
    legendTrack: "przypadki w biegu",
    legendNote: "jedna kolumna na bieg · kliknij, żeby otworzyć",
  },

  audit: {
    heading: "Oś czasu ławki",
    loading: "Wczytywanie audytu…",
    heldNowBy: "trzyma teraz <b>{{actor}}</b>",
    heldNowByFor: "trzyma teraz <b>{{actor}}</b> — {{purpose}}",
    lapsed:
      "wolna — dzierżawa <b>{{actor}}</b> wygasła {{when}} bez zwolnienia",
    free: "wolna — nikt nie trzyma tej ławki",
    leasesOnly: "tylko dzierżawy i biegi — nie przysłano jeszcze zapisów dostępu",
    neverReported:
      "nigdy nie zgłoszono — ta ławka jest nieaudytowana, nie bezczynna",
    lastAccess: "ostatni dostęp {{when}}",
    looseWrites_one: "{{count}} zapis bez własnej dzierżawy",
    looseWrites_few: "{{count}} zapisy bez własnej dzierżawy",
    looseWrites_many: "{{count}} zapisów bez własnej dzierżawy",
    looseWrites_other: "{{count}} zapisów bez własnej dzierżawy",
    noPurpose: "nie podano celu",
    legendHeld: "trzymana (odcienie zmieniają się, żeby oddzielić dzierżawy)",
    legendWrite: "zapis",
    legendRead: "odczyt",
    legendTaken: "przejęta",
    legendDenied: "odmowa",
    colWhen: "kiedy",
    colKind: "rodzaj",
    colWhat: "co",
    colWho: "kto",
    colDetail: "szczegóły",
    noLease: "· bez dzierżawy",
    empty: "Dla tej ławki nic jeszcze nie zapisano.",
    kind: { lease: "dzierżawa", access: "dostęp", run: "bieg" },
    event: {
      acquired: "wzięta",
      released: "zwolniona",
      expired: "wygasła",
      taken: "przejęta",
      denied: "odmowa",
      peek: "podejrzenie",
      running: "trwa",
    },
  },

  people: {
    accountCount_one: "{{count}} konto",
    accountCount_few: "{{count}} konta",
    accountCount_many: "{{count}} kont",
    accountCount_other: "{{count}} konta",
    groupCount_one: "{{count}} grupa",
    groupCount_few: "{{count}} grupy",
    groupCount_many: "{{count}} grup",
    groupCount_other: "{{count}} grupy",
    title: "Ludzie",
    heading: "Ludzie",
    tabAccounts: "Konta",
    tabGroups: "Grupy",
    intro:
      "Role nadaje się w projekcie albo na ławce, ze strony tego projektu lub tej ławki. Ta strona mówi, kto istnieje i kto jest w którym zespole.",
    passwordFor: "Hasło dla <b>{{who}}</b>: <code>{{pw}}</code>",
    passwordOnce:
      "Pokazane raz — jest przechowywane jako skrót, więc to jedyna kopia. Przy pierwszym logowaniu ta osoba zostanie poproszona o zmianę.",
    newAccount: "Nowe konto",
    username: "Nazwa użytkownika",
    usernamePlaceholder: "dgugala",
    fullName: "Imię i nazwisko",
    fullNamePlaceholder: "Dariusz Gugała",
    instanceAdmin: "administrator instancji",
    create: "Utwórz",
    createNote:
      "Hasło jest generowane i pokazywane raz. Konto musi je zmienić przy pierwszym logowaniu.",
    badgeAdmin: "admin",
    badgeSso: "sso",
    badgeDeactivated: "nieaktywne",
    hide: "ukryj",
    reach: "do czego ma dostęp",
    resetPassword: "zresetuj hasło",
    makeAdmin: "nadaj admina",
    revokeAdmin: "odbierz admina",
    deactivate: "dezaktywuj",
    reactivate: "aktywuj ponownie",
    lastSignedIn: "ostatnie logowanie {{when}}",
    inGroups_one: " · w {{count}} grupie",
    inGroups_few: " · w {{count}} grupach",
    inGroups_many: " · w {{count}} grupach",
    inGroups_other: " · w {{count}} grupach",
    mustChange: " · musi zmienić hasło",
    noAccounts:
      "Jeszcze żadnych kont. Dopóki ich nie ma, ta instancja jest otwarta (albo chroniona wspólnym hasłem startowym) — utworzenie pierwszego konta ją zamyka.",
    noGrants:
      "Brak nadań. Nadal może czytać każdy projekt oznaczony jako wewnętrzny.",
    grantOn: "<b>{{role}}</b> w <scopeLink>{{scope}}</scopeLink>",
    throughGroup: " — przez grupę {{group}}",
    grantedDirectly: " — nadane bezpośrednio",
    newGroup: "Nowa grupa",
    groupNamePlaceholder: "sncf-testers",
    noGroups:
      "Jeszcze żadnych grup. Grupa to zespół: włóż do niej ludzi, a potem nadaj grupie rolę w projekcie.",
    memberCount_one: "{{count}} osoba",
    memberCount_few: "{{count}} osoby",
    memberCount_many: "{{count}} osób",
    memberCount_other: "{{count}} osób",
    grantCount_one: "{{count}} nadanie",
    grantCount_few: "{{count}} nadania",
    grantCount_many: "{{count}} nadań",
    grantCount_other: "{{count}} nadań",
    groupPeople: "Ludzie",
    manager: "kierownik",
    remove: "usuń",
    groupEmpty: "Nikogo jeszcze w tej grupie.",
    addToGroup: "dodaj do grupy",
    addSomebody: "dodaj kogoś…",
    add: "Dodaj",
    whereRole: "Gdzie ta grupa ma rolę",
    noWhere: "Jeszcze nigdzie. Nadaj ją ze strony projektu albo ławki.",
    groupNote:
      "Każdy w tej grupie ma {{what}} wszędzie tam, gdzie grupa ma nadanie. Role: {{ladder}}.",
    whatAbove: "role wypisane wyżej",
    whatWhatever: "to, co grupie nadano",
  },

  now: {
    answerAttention_one: "{{count}} sprawa wymaga uwagi.",
    answerAttention_few: "{{count}} sprawy wymagają uwagi.",
    answerAttention_many: "{{count}} spraw wymaga uwagi.",
    answerAttention_other: "{{count}} sprawy wymaga uwagi.",
    answerRunning_one: "Trwa {{count}} bieg.",
    answerRunning_few: "Trwają {{count}} biegi.",
    answerRunning_many: "Trwa {{count}} biegów.",
    answerRunning_other: "Trwa {{count}} biegu.",
    onlineCount: "{{count}} w sieci",
    lastRun: "Ostatni bieg ruszył {{when}}.",
    onlineFree: "w sieci, wolna",
    onlineHeld: "w sieci, zajęta",
    answerQuiet: "Nic nie biegnie i nic świeżo nie pękło.",
    headingBuilds: "Wersje",
    buildNamed: "Wersja {{build}}",
    installedOn: "Wersja zainstalowana na {{bench}}",
    buildGreen: "{{build}}: zaplanowano {{planned}}, wykonano {{ran}}, nic nie zawiodło.",
    buildRunning: "{{build}}: kampania trwa, wykonano {{ran}} z {{planned}}.",
    buildCancelled: "{{build}}: kampanię anulowano po {{ran}} z {{planned}}.",
    buildFailed_one: "{{build}}: {{count}} test zawiódł (wykonano {{ran}} z {{planned}}).",
    buildFailed_few: "{{build}}: {{count}} testy zawiodły (wykonano {{ran}} z {{planned}}).",
    buildFailed_many: "{{build}}: {{count}} testów zawiodło (wykonano {{ran}} z {{planned}}).",
    buildFailed_other: "{{build}}: {{count}} testu zawiodło (wykonano {{ran}} z {{planned}}).",
    buildNotAllRan: "{{build}}: nic nie zawiodło, ale wykonano tylko {{ran}} z {{planned}} zaplanowanych przypadków.",
    buildLost: "{{build}}: bieg stanął, zanim testy zgłosiły wyniki (wykonano {{ran}} z {{planned}}).",
    buildOnlyKnown: "{{build}}: nic nowego nie zawiodło (wykonano {{ran}} z {{planned}}). Znane problemy: {{n}}.",
    openCampaign: "Otwórz kampanię",
    title: "Teraz",
    heading: "Teraz",
    rigs_one: "{{count}} ławka",
    rigs_few: "{{count}} ławki",
    rigs_many: "{{count}} ławek",
    rigs_other: "{{count}} ławki",
    running: "{{count}} w biegu",
    idle: "bezczynne",
    headingRunning: "W biegu",
    headingAttention: "Wymaga uwagi",
    headingRigs: "Ławki",
    allQuiet:
      "Nic nie biegnie i nic świeżo nie pękło. Ławki i ich ostatnie biegi są niżej.",
    noBenches:
      "Jeszcze żadnych ławek. Utwórz jedną, a potem zarejestruj przy niej agenta.",
    brokeLabel_one: "{{count}} nowe niepowodzenie",
    brokeLabel_few: "{{count}} nowe niepowodzenia",
    brokeLabel_many: "{{count}} nowych niepowodzeń",
    brokeLabel_other: "{{count}} nowych niepowodzeń",
    brokeTitle: "<runLink>{{run}}</runLink> na {{bench}} · {{when}}",
    gateLabel: "bramka harnessu nie przeszła",
    gateTitle:
      "<runLink>{{run}}</runLink> zmierzony harnessem, który sam się nie trzyma",
    noJunitLabel: "brak wyników",
    noJunitTitle:
      "<runLink>{{run}}</runLink> skończył się bez wysłania werdyktów, więc jego liczby to nieudane kontrole",
    leaseLabel: "dzierżawa po terminie",
    leaseTitle: "<b>{{actor}}</b> nadal trzyma <benchLink>{{bench}}</benchLink>",
    leaseTitleFor:
      "<b>{{actor}}</b> nadal trzyma <benchLink>{{bench}}</benchLink> — {{purpose}}",
    quietLabel: "cisza",
    quietAuto: "Automatyczne testy są tu włączone, więc ich kampanie też stanęły.",
    quietTitle: "<benchLink>{{bench}}</benchLink> nie zgłasza się od {{when}}",
    selfTests: "autotesty harnessu",
    stateOnline: "online",
    whyOnline: "agent widziany w ciągu ostatnich 2 minut",
    statePublishes: "tylko publikuje",
    whyPublishes:
      "żaden agent nigdy się tu nie zgłosił: biegi są publikowane, ale nic nie da się tu zlecić",
    stateOffline: "offline",
    whyOffline: "brak świeżego sygnału",
    heldBy: "trzyma <b>{{actor}}</b>",
    heldByFor: "trzyma <b>{{actor}}</b> — {{purpose}}",
    free: "wolne",
    seen: "widziane {{when}}",
    neverSeen: "nigdy nie widziane",
    nothingReported: "Nic jeszcze nie zgłoszono.",
    runsLink: "Biegi",
    takeAndRun: "Rozpocznij testy",
  },

  projects: {
    campaignsHeading: "Kampanie",
    allCampaigns: "Wszystkie kampanie tego projektu",
    noCampaigns: "W tym projekcie nie było jeszcze żadnej kampanii.",
    projectCount_one: "{{count}} projekt",
    projectCount_few: "{{count}} projekty",
    projectCount_many: "{{count}} projektów",
    projectCount_other: "{{count}} projektów",
    title: "Projekty",
    heading: "Projekty",
    newProject: "Nowy projekt",
    namePlaceholder: "Nazwa projektu",
    create: "Utwórz",
    emptyAdmin: "Jeszcze żadnych projektów. Utwórz jeden, a potem włóż do niego ławki.",
    emptyReader:
      "Nic tu jeszcze dla ciebie — poproś o rolę w projekcie albo na którejś z jego ławek.",
    privateBadge: "Prywatny",
    privateTitle: "ten projekt widzą tylko jego członkowie",
    yourRoleShort: "ty: {{role}}",
    benchCount_one: "{{count}} ławka",
    benchCount_few: "{{count}} ławki",
    benchCount_many: "{{count}} ławek",
    benchCount_other: "{{count}} ławek",
    runCount_one: "{{count}} bieg",
    runCount_few: "{{count}} biegi",
    runCount_many: "{{count}} biegów",
    runCount_other: "{{count}} biegów",
    backToProjects: "← projekty",
    fallbackTitle: "Projekt {{id}}",
    yourRole: "twoja rola: {{role}}",
    editDescription: "Edytuj opis",
    scenariosLink: "Scenariusze",
    descriptionLabel: "Opis projektu",
    noDescription:
      "Jeszcze bez opisu. Napisz, czym jest ten projekt, gdzie stoją jego ławki i co musi wiedzieć ktoś nowy.",
    benchesHeading: "Ławki ({{count}})",
    noBenches: "Jeszcze żadnych — utwórz ławkę na stronie Ławki i przypisz ją tutaj.",
    lastSeen: "ostatnio widziana {{when}}",
    visPrivate: "prywatny — widzą go tylko jego członkowie",
    visInternal: "wewnętrzny — czyta go każdy zalogowany",
    makeInternal: "{{label}} · zmień na wewnętrzny",
    makePrivate: "{{label}} · zmień na prywatny",
  },

  scenarios: {
    viewCases: "Wszystkie przypadki",
    viewLabel: "co pokazać o tych scenariuszach",
    answerCases_one: "{{count}} przypadek testowy.",
    answerCases_few: "{{count}} przypadki testowe.",
    answerCases_many: "{{count}} przypadków testowych.",
    answerCases_other: "{{count}} przypadków testowych.",
    heading: "Scenariusze",
    pageTitle: "Scenariusze · {{project}}",
    back: "← {{project}}",
    empty:
      "Żaden bieg na ławkach tego projektu nie zapisał jeszcze swoich scenariuszy. Lista pojawi się po pierwszym biegu, który je wyśle.",
    openNotHere: "{{id}} nie ma na tej liście. Wybierz inną listę powyżej.",
    buildUnknown: "build nieznany",
    lists_one: "{{count}} lista — po jednej na wersję danych testowych",
    lists_few: "{{count}} listy — po jednej na wersję danych testowych; otwarta najnowsza",
    lists_many: "{{count}} list — po jednej na wersję danych testowych; otwarta najnowsza",
    lists_other: "{{count}} listy — po jednej na wersję danych testowych; otwarta najnowsza",
    fromYourRun: "twój bieg",
    asOf: "Tak zapisał {{run}} na {{bench}}, {{when}}, build {{build}}.",
    openRun: "otwórz bieg",
    testData: "dane testowe {{digest}}",
    noFingerprint: "bez odcisku danych testowych",
    against: "Wobec {{run}}: dodane {{added}}, usunięte {{removed}}.",
    againstSame: "Te same przypadki co w {{run}}.",
    outsidePlan: "Poza planem",
    filter: "Filtr: id, nazwa, czasownik, wymaganie",
    shown: "widać {{shown}} z {{count}}",
    never: "{{bench}}: nigdy nie zgłoszony",
    lastOn: "{{bench}}: {{verdict}} w {{run}}",
    lastRan: "ostatnio wykonany w {{run}}: {{verdict}}",
    ranBefore: "wcześniej {{verdict}}",
    draft: "szkic",
    requires: "wymaga: {{list}}",
    tree: {
      heading: "Według klienta i ekranu",
      intro:
        "Każdy profil, który ławka przygotowuje, i co powiedział jego najnowszy bieg: najpierw testy ogólne, potem każdy klient i każdy jego ekran. Przypadki pod biegiem to te, które ocenił, więc każdy przypadek jest w jednym miejscu.",
      general: "Testy ogólne",
      recordedJourney: "Nagrana podróż",
      storyboards: "Storyboardy",
      simulated: "Dane symulowane",
      fixture: "Fixture",
      cases_one: "{{count}} przypadek",
      cases_few: "{{count}} przypadki",
      cases_many: "{{count}} przypadków",
      cases_other: "{{count}} przypadku",
      onBench: "na {{bench}}",
      never: "Jeszcze tu nie biegł.",
      last: "{{run}} na {{build}}, {{when}}: {{result}}.",
      result: "{{passed}} zaliczonych, {{failed}} niezaliczonych, {{xfailed}} oczekiwanych porażek, {{skipped}} nieuruchomionych",
      campaignGreen: "wszystko przebiegło, nic nie padło",
      campaignNotGreen: "nie wszystko zielone",
      campaignRunning: "w toku",
      campaignCancelled: "przerwana",
      show: "pokaż przypadki",
      hide: "ukryj przypadki",
      notInAny_one: "{{count}} przypadek z listy poniżej nie jest jeszcze w żadnej gałęzi: {{list}}",
      notInAny_few: "{{count}} przypadki z listy poniżej nie są jeszcze w żadnej gałęzi: {{list}}",
      notInAny_many: "{{count}} przypadków z listy poniżej nie jest jeszcze w żadnej gałęzi: {{list}}",
      notInAny_other: "{{count}} przypadku z listy poniżej nie jest jeszcze w żadnej gałęzi: {{list}}",
      inSeveral: "W więcej niż jednej gałęzi: {{list}}",
    },
    plan: {
      objective: "cel",
      preconditions: "warunki wstępne",
      expected_result: "oczekiwany wynik",
    },
    asWritten: "jak napisano · {{source}}",
    noSource: "Ten bieg nie zapisał treści przypadku.",
    alsoSame: "Ta sama treść w {{source}}.",
    alsoDiffers: "Inna treść w {{source}}.",
    notRecorded: "Żadna lista na tej stronie nie ma treści tego przypadku.",
    history: "wyniki wg ławki, od najnowszego",
    expected: "Czego oczekuje, jak porównano w {{run}}",
    expectedAlt: "Zatwierdzony ekran {{golden}}",
    noHistory: "nigdy nie zgłoszony",
  },

  scenarioView: {
    readable: "Czytelnie",
    source: "Źródło",
    precondition: "Przed krokami",
    restore: "Na koniec przywraca",
    captured: "zapamiętane wcześniej: {{what}}",
    checks: "Sprawdza",
    saves: "Zapisuje",
    root: "jako root",
    draft: "szkic",
    runsLast: "biegnie na końcu",
    script_one: "skrypt, {{count}} linia",
    script_few: "skrypt, {{count}} linie",
    script_many: "skrypt, {{count}} linii",
    script_other: "skrypt, {{count}} linii",
    rects_one: "{{count}} prostokąt",
    rects_few: "{{count}} prostokąty",
    rects_many: "{{count}} prostokątów",
    rects_other: "{{count}} prostokąta",
    unplaced: "Komentarzy tego bloku nie dało się pewnie przypisać do kroków, więc są tylko w źródle.",
  },
  benches: {
    title: "Ławki",
    heading: "Ławki",
    newBench: "Nowa ławka",
    tokenNote:
      "Token dla <b>{{name}}</b> — skopiuj go teraz, nie pokaże się drugi raz, a przechowywany jest tylko jego skrót.",
    dismiss: "schowaj",
    empty: "Jeszcze żadnych ławek. Utwórz jedną, a potem zarejestruj przy niej agenta.",
    noTokenBadge: "brak tokenu",
    noTokenTitle: "dla tej ławki nie wydano jeszcze tokenu",
    heldBy: "trzyma <b>{{actor}}</b>",
    heldByFor: "trzyma <b>{{actor}}</b> — {{purpose}}",
    lastSeen: "ostatnio widziana",
    runs: "biegi",
    running: "w biegu",
    agent: "agent",
    open: "Otwórz",
    runsLink: "Biegi",
    edit: "Edytuj",
    newToken: "Nowy token",
    getToken: "Pobierz token",
    editTitle: "Edycja: {{name}}",
    deleteTitle: "Usuń {{name}}",
    onlineTitle: "agent widziany w ciągu ostatnich 2 minut",
    offlineTitle: "brak świeżego sygnału",
    name: "Nazwa",
    description: "Opis",
    deleteWarn_one:
      "Usunie to również <b>{{count}}</b> bieg zapisany dla tej ławki. Nie da się tego cofnąć. Wpisz <name>{{name}}</name>, żeby potwierdzić.",
    deleteWarn_few:
      "Usunie to również <b>{{count}}</b> biegi zapisane dla tej ławki. Nie da się tego cofnąć. Wpisz <name>{{name}}</name>, żeby potwierdzić.",
    deleteWarn_many:
      "Usunie to również <b>{{count}}</b> biegów zapisanych dla tej ławki. Nie da się tego cofnąć. Wpisz <name>{{name}}</name>, żeby potwierdzić.",
    deleteWarn_other:
      "Usunie to również <b>{{count}}</b> biegów zapisanych dla tej ławki. Nie da się tego cofnąć. Wpisz <name>{{name}}</name>, żeby potwierdzić.",
  },

  benchDetail: {
    qualityLink: "Ile dowodzą jej testy",
    answerFree: "Wolna i w sieci.",
    answerHeld: "Zajęta przez {{actor}}.",
    fallbackTitle: "Ławka {{id}}",
    back: "← ławki",
    yourRole: "twoja rola: {{role}}",
    yourRoleTitle: "na tej ławce, wprost albo przez jej projekt",
    factSeen: "widziana",
    factAgent: "agent",
    factRuns: "biegi",
    factCaps: "możliwości",
    factSince: "od",
    factRunning: "w biegu",
    never: "nigdy",
    noCapsReported: "nic nie zgłoszono",
    nothingReported: "Nic jeszcze nie zgłoszono.",
    colRun: "Bieg",
    colStarted: "Start",
    colTook: "Trwał",
    colSteps: "Kroki",
    colResult: "Wynik",
    allRuns: "Wszystkie biegi na tej ławce",
    allRunsTail: " — z filtrami i resztą historii.",
    notes: "Notatki",
    editNotes: "Edytuj notatki",
    notesLabel: "{{name}} — notatki",
    noNotes:
      "Jeszcze bez notatek. Adresy, okablowanie, jakiego hasła chce, co się na niej psuje.",
    tablistLabel: "co pokazać o tej ławce",
    tabRuns: "Biegi",
    tabSetup: "Ustawienia",
    tabAccess: "Dostęp",
    tabNotes: "Notatki",
    tabTimeline: "Oś czasu",
    heldBy: "Trzyma {{actor}}",
    minutesLeft: "zostało {{count}} min",
    pastDeadline: "po terminie",
    quietFor: " · cisza od {{count}} min",
    release: "Zwolnij…",
    taken: "wzięta {{when}}",
    confirmUse: "<b>{{actor}}</b> pracuje na tej ławce. Zwolnienie jej teraz może zepsuć trwający bieg, a przejęcie zostanie zapisane na ciebie.",
    confirmUseFor: "<b>{{actor}}</b> pracuje na tej ławce: {{purpose}}. Zwolnienie jej teraz może zepsuć trwający bieg, a przejęcie zostanie zapisane na ciebie.",
    leaveIt: "Zostaw",
    takeIt: "Przejmij ławkę",
    whoHasHadIt: "kto ją miał ({{count}})",
    tookFrom: " (od: {{actor}})",
  },

  quality: {
    answerAllFresh: "{{count}} grup przejrzanych, wszystkie świeże.",
    title: "Jakość",
    heading: "Jakość",
    intro:
      "Osąd z przeglądu, a nie odsetek zaliczeń.",
    empty: "Nie opublikowano jeszcze żadnego przeglądu. Ławka wyśle go z następnym biegiem.",
    whichBench: "która ławka",
    noReview: " (bez przeglądu)",
    fromBench: "z {{bench}}",
    received: "odebrano {{when}}",
    staleCount: "{{count}} z {{total}} przeglądów jest nieaktualnych",
    chipWeak: "C lub gorzej ({{count}})",
    chipStale: "Nieaktualne ({{count}})",
    colGrade: "Ocena",
    colGroup: "Grupa",
    colScenario: "Ze scenariuszem",
    colEverPassed: "Kiedykolwiek zaliczone tutaj",
    colConfidence: "Pewność",
    staleBadge: "nieaktualne",
    staleTitle: "scenariusze zmieniły się od ostatniego spojrzenia",
    everPassedTitle: "odpowiedź z wszystkich biegów, jakie ta usługa dostała",
    neverSuffix: " · {{count}} nigdy",
    gaps: "Luki",
    recommendations: "Zalecenia",
    neverPassed: "Nigdy tu nie zaliczone:",
    reviewed: "przejrzane {{when}}",
    reviewedBy: ", autor: {{who}}",
    gloss: {
      A: "dowodzi tego, co deklaruje",
      B: "trzyma się",
      C: "działa, ale niewiele dowodzi",
      D: "dowód jest tylko pozorny",
      E: "żadnego dowodu",
    },
  },

  runs: {
    answerLoaded_one: "Wczytany {{count}} bieg.",
    answerLoaded_few: "Wczytane {{count}} biegi.",
    answerLoaded_many: "Wczytanych {{count}} biegów.",
    answerLoaded_other: "Wczytanych {{count}} biegu.",
    answerFailures_one: "W {{count}} są niepowodzenia.",
    answerFailures_few: "W {{count}} są niepowodzenia.",
    answerFailures_many: "W {{count}} są niepowodzenia.",
    answerFailures_other: "W {{count}} są niepowodzenia.",
    answerNoFailures: "W żadnym nic nie padło.",
    title: "Biegi",
    heading: "Biegi",
    searchPlaceholder: "szukaj biegu po nazwie",
    searchLabel: "szukaj biegów po nazwie",
    allBenches: "Wszystkie ławki",
    allProducts: "Wszystkie produkty",
    hasFailures: "Z niepowodzeniami",
    clean: "Czyste",
    wholeSuite: "Cały zestaw",
    slices: "Wycinki",
    empty:
      "Nic jeszcze nie zgłoszono. Bieg pojawia się tutaj, gdy tylko jego agent go otworzy.",
    colRun: "Bieg",
    colBench: "Ławka",
    colBuild: "Wersja",
    colStarted: "Start",
    colTook: "Trwał",
    colSteps: "Kroki",
    colResult: "Wynik",
    loadedFiltered: "{{shown}} z {{total}} wczytanych",
    loaded: "wczytane: {{count}}",
    moreOnServer: ", na serwerze jest więcej",
    thatIsAll: ", to wszystkie",
    clearFilters: "wyczyść filtry",
    loadMore: "Wczytaj kolejne {{count}}",
    filteringNote_one:
      "Filtrowany jest {{count}} bieg wczytany do tej pory, nie cała historia.",
    filteringNote_few:
      "Filtrowane są {{count}} biegi wczytane do tej pory, nie cała historia.",
    filteringNote_many:
      "Filtrowanych jest {{count}} biegów wczytanych do tej pory, nie cała historia.",
    filteringNote_other:
      "Filtrowanych jest {{count}} biegów wczytanych do tej pory, nie cała historia.",
    today: "Dzisiaj",
    yesterday: "Wczoraj",
    dayLabel: "{{weekday}} {{day}} {{month}}",
    dayLabelWithYear: "{{weekday}} {{day}} {{month}} {{year}}",
  },

  manual: {
    answerCases_one: "{{count}} przypadek robiony ręcznie.",
    answerCases_few: "{{count}} przypadki robione ręcznie.",
    answerCases_many: "{{count}} przypadków robionych ręcznie.",
    answerCases_other: "{{count}} przypadków robionych ręcznie.",
    title: "Praca ręczna",
    heading: "Praca ręczna",
    empty:
      "Nic jeszcze nie opublikowano. Ten dokument powstaje na ławce przy starcie biegu — pojawi się więc po następnym biegu na ławce, której harness go eksportuje, a nigdy na takiej, której harness jest od tego starszy.",
    benchOn: "na {{bench}}",
    published: "opublikowano {{when}}",
    notKnown: "ławka nieznana",
    minutes: "{{count}} min",
    shed_one:
      "{{count}} przypadek odpadł, żeby się zmieściło — dokument był za duży, żeby zapisać go w całości.",
    shed_few:
      "{{count}} przypadki odpadły, żeby się zmieściło — dokument był za duży, żeby zapisać go w całości.",
    shed_many:
      "{{count}} przypadków odpadło, żeby się zmieściło — dokument był za duży, żeby zapisać go w całości.",
    shed_other:
      "{{count}} przypadków odpadło, żeby się zmieściło — dokument był za duży, żeby zapisać go w całości.",
    jiraAbsent:
      "Na ławce nie ma danych z Jiry, więc żaden przypadek niżej nie niesie werdyktu o pokryciu. Procedury i tak są poprawne — pochodzą z planu.",
    jiraStale:
      "Migawka Jiry ma {{days}} dni (pobrana {{when}}). Wszystko, co zmieniło się w XSNCF od tego czasu, jest tu niewidoczne.",
    capsUnknown:
      "Możliwości ławki nie były znane, gdy to liczono, więc nic nie jest oznaczone jako pomijane tutaj — nie dlatego, że nic nie jest.",
    metaPriority: "priorytet {{priority}}",
    metaLevel: "wymaga dostępu {{level}}",
    metaMinutes: "~{{count}} min",
    metaNeeds: "wymaga: {{caps}}",
    before: "Zanim zaczniesz:",
    procedure: "Procedura — kroków: {{total}}, ręcznie: {{manual}}",
    furtherSteps: "Dalszych kroków nie pokazano: {{count}}.",
    passWhen: "Zaliczone, gdy:",
    jira: "Jira",
    closed: " (zamknięte)",
    colField: "Pole",
    colJira: "Jira mówi",
    colPlan: "Plan mówi",
    wordingDiffers:
      "różnice w brzmieniu ({{count}}) — przeczytaj, niekoniecznie zmieniaj",
    issueProblems: "Problemy ze zgłoszeniami",
    action: "Działanie:",
    verdict: {
      synced: "zgodne",
      drifted: "rozjechane",
      unknown: "nieznane",
      missing: "brak",
    },
  },

  launch: {
    tagRig: "skrypty",
    tagTests: "testy",
    tagAlias: "inna nazwa buildu",
    title: "Najpierw zbuduj nową wersję",
    noTarget:
      "Ten projekt nie ma skonfigurowanego celu uruchamiania, więc nic się stąd jeszcze nie odpali.",
    noToken:
      "Ten serwer nie ma poświadczenia do uruchamiania: <code>{{env}}</code> nie jest ustawione. Ustawia je operator na hoście; token nigdy nie jest tu przechowywany.",
    target: "Prosi <code>{{project}}</code> na <code>{{ref}}</code> o zbudowanie i uruchomienie.",
    onRig: "już na tej ławce — sekundy",
    readyNow: "gotowe od ręki",
    needsBuild: "wymaga zbudowania (~15 min)",
    typeIt: "— wpisz wartość —",
    partialList: "Części wartości nie udało się wypisać: {{why}}. Możesz ją wpisać ręcznie.",
    emptyList: "Nie ma jeszcze z czego wybierać. Wpisz wartość ręcznie.",
    noList: "Brak listy: {{why}}. Wpisz wartość ręcznie.",
    whichRig: "O tym, na którą ławkę trafi, decyduje potok; ten formularz jej nie wybiera.",
    reason: "Po co — następna osoba to przeczyta, tak jak cel dzierżawy",
    start: "Zbuduj i uruchom",
    starting: "Proszę…",
    cost: "Buduje wersję i uruchamia ją na ławce — to kończy się ruchem sprzętu.",
    needOperator: "Budowanie może uruchomić operator lub wyżej.",
    needFields: "Brakuje jeszcze: {{fields}}",
    recorded: "Próba została zapisana jako #{{id}}, niżej.",
    opened: "Pipeline #{{id}} otwarty",
    history_one: "{{count}} wcześniejsze uruchomienie",
    history_few: "{{count}} wcześniejsze uruchomienia",
    history_many: "{{count}} wcześniejszych uruchomień",
    history_other: "{{count}} wcześniejszych uruchomień",
  },
  testSelections: {
    title: "Oferowane testy",
    usingDefaults: "domyślne serwisu",
    count_one: "{{count}} pozycja ustawiona przez projekt",
    count_few: "{{count}} pozycje ustawione przez projekt",
    count_many: "{{count}} pozycji ustawionych przez projekt",
    count_other: "{{count}} pozycji ustawionych przez projekt",
    what: "Lista „Testy” w formularzu biegu na każdym rigu tego projektu. Marker trafia do pytesta; etykietę widzą ludzie.",
    marker: "marker pytesta",
    label: "Etykieta",
    labelPlaceholder: "to, co widzą ludzie",
    add: "Dodaj pozycję",
    save: "Zapisz listę",
    saving: "Zapisuję…",
    useDefaults: "Wróć do domyślnych serwisu",
    blank: "Każda pozycja potrzebuje markera i etykiety.",
    needMaintainer: "Tę listę może zmienić maintainer lub wyżej.",
    up: "w górę",
    down: "w dół",
    remove: "usuń",
  },
  reports: {
    answerSaved_one: "{{count}} zapisany raport.",
    answerSaved_few: "{{count}} zapisane raporty.",
    answerSaved_many: "{{count}} zapisanych raportów.",
    answerSaved_other: "{{count}} zapisanego raportu.",
    title: "Raporty",
    heading: "Raporty",
    newReport: "Nowy raport",
    intro:
      "Zakres i uporządkowana lista sekcji, zapisana i współdzielona. Szczegółowość wybiera się osobno dla każdej sekcji, więc „niepowodzenia w całości, reszta jako lista” to raport, który można zachować.",
    sectionCount_one: "{{count}} sekcja",
    sectionCount_few: "{{count}} sekcje",
    sectionCount_many: "{{count}} sekcji",
    sectionCount_other: "{{count}} sekcji",
    edited: " · zmieniony {{when}}",
    editedBy: ", autor: {{who}}",
    delete: "usuń",
    empty: "Nie ma jeszcze zapisanych raportów.",
    lookLegend: "Wygląd i marka",
    lookNote:
      "Stosowane do każdego renderowanego raportu, HTML i PDF tak samo. Kolory wyników domyślnie biorą paletę pulpitu ławek, sprawdzoną pod kątem rozróżnialności dla wad widzenia barw — nadpisuj je świadomie.",
    brand: "Marka",
    accent: "Kolor akcentu",
    accentLabel: "kolor akcentu",
    strapline: "Hasło (pasek nagłówka i stopka każdego PDF-a)",
    verdictColours: "Kolory wyników",
    verdictFg: "kolor tekstu: {{verdict}}",
    verdictBg: "kolor tła: {{verdict}}",
    saveLook: "Zapisz wygląd",
    resetDefaults: "Przywróć domyślne",
    name: "Nazwa",
    whatFor: "Do czego służy",
    whatForHint: "— strona biegu pokazuje to po najechaniu",
    scope: "Zakres",
    campaign: "Kampania",
    campaignNone: "— żadna: ostatnie N biegów —",
    campaignOption: "#{{id}} · {{build}} na {{bench}} · {{status}}",
    campaignScope: "Biegi kampanii",
    campaignDrop: "odrzuć kampanię",
    campaignStatus: {
      running: "jeszcze w toku",
      green: "wszystko przebiegło, nic nie padło",
      not_green: "nie wszystko zielone",
      cancelled: "przerwana",
    },
    lastRuns: "Ostatnich N biegów",
    benches: "Ławki",
    any: "dowolna",
    from: "Od",
    to: "Do",
    casesMatching: "Przypadki pasujące (glob, np. CERT-*)",
    verdicts: "Werdykty",
    verdictsHint:
      "— nic nie zaznaczone znaczy wszystkie werdykty. Zaznaczenie części znaczy TYLKO te, a to łatwo zostawić w połowie: raport nazwany „pełny” z zaznaczonym tylko <code>passed</code> nie zawiera ani jednego niepowodzenia.",
    sectionsLegend: "Sekcje, po kolei",
    sectionType: "rodzaj sekcji",
    detail: "szczegółowość",
    layout: "układ",
    moveUp: "przenieś w górę",
    moveDown: "przenieś w dół",
    removeSection: "usuń sekcję",
    notesText: "treść notatki",
    addSection: "Dodaj sekcję",
    preview: "Podgląd",
    previewTitle: "podgląd raportu",
    previewPrompt: "Naciśnij Podgląd, żeby wyrenderować tę definicję.",
    deleteTitle: "Usunąć {{name}}?",
    deleteWarn:
      "Wszyscy tracą tę definicję, a każda strona biegu traci przycisk, który ją renderuje. Pobrane wcześniej dokumenty zostają — to usuwa przepis, a nie to, co z niego powstało.",
    keepIt: "Zostaw",
  },

  runDetail: {
    casePage: "Strona przypadku",
    took: "trwał {{duration}}",
    answerRunning: "Jeszcze trwa.",
    answerAwaiting: "Zakończony; wyniki są jeszcze w drodze.",
    answerLost: "Zakończony, ale wyniki nigdy nie dotarły.",
    notComparedNoProfile: "Wyniki nie są porównane z wcześniejszym biegiem: ten bieg nie zapisał, jakim profilem biegł.",
    notComparedNoData: "Wyniki nie są porównane z wcześniejszym biegiem: ten bieg nie zapisał, jakich danych testowych użył.",
    notComparedData: "Wyniki nie są porównane z <runLink>{{run}}</runLink>, poprzednim biegiem tego profilu: użył innych danych testowych, więc zmiana wyniku mogłaby wynikać z danych, a nie z produktu.",
    askedBy: "na prośbę: {{who}}",
    partOfCampaign: "część kampanii",
    movedWhatTests: "testy",
    movedSentenceProfile: "Od <runLink>{{run}}</runLink>, poprzedniego biegu tego profilu, zmieniło się: {{what}}.",
    testData: "dane testowe {{digest}}",
    testDataTitle: "Odcisk danych testowych profilu: nagrania, obrazy wzorcowe, paczka układu",
    answerNothingFailed: "Nic nie zawiodło.",
    answerAllKnown_one: "{{count}} porażka, znany problem.",
    answerAllKnown_few: "Wszystkie {{count}} porażki to znane problemy.",
    answerAllKnown_many: "Wszystkie {{count}} porażek to znane problemy.",
    answerAllKnown_other: "Wszystkie {{count}} porażki to znane problemy.",
    answerFailed_one: "{{count}} test zawiódł.",
    answerFailed_few: "{{count}} testy zawiodły.",
    answerFailed_many: "{{count}} testów zawiodło.",
    answerFailed_other: "{{count}} testu zawiodło.",
    answerPassed_one: "{{count}} zaliczony",
    answerPassed_few: "{{count}} zaliczone",
    answerPassed_many: "{{count}} zaliczonych",
    answerPassed_other: "{{count}} zaliczonego",
    answerExpected_one: "{{count}} oczekiwane niepowodzenie",
    answerExpected_few: "{{count}} oczekiwane niepowodzenia",
    answerExpected_many: "{{count}} oczekiwanych niepowodzeń",
    answerExpected_other: "{{count}} oczekiwanego niepowodzenia",
    answerNotRun_one: "{{count}} nieuruchomiony tutaj",
    answerNotRun_few: "{{count}} nieuruchomione tutaj",
    answerNotRun_many: "{{count}} nieuruchomionych tutaj",
    answerNotRun_other: "{{count}} nieuruchomionego tutaj",
    buildNamed: "wersja {{build}}",
    details: "Szczegóły: wersja, dane testowe, autotesty",
    movedWhatBuild: "wersja",
    movedWhatHarness: "framework testów",
    movedWhatData: "dane testowe",
    movedSentence: "Od <runLink>{{run}}</runLink> zmieniło się: {{what}}.",
    back: "← biegi",
    fallbackTitle: "Bieg {{id}}",
    pageTitle: "{{run}} na {{bench}}",
    onBench: "na {{bench}}",
    finished: "zakończony",
    running: "trwa",
    deleteThisRun: "usuń ten bieg",
    sameVariantBuild: "Ten sam {{variant}} na innej wersji ({{from}} → {{to}}), porównanie z",
    variantFallback: "wariant",
    tileScenarios: "Wszystkie przypadki",
    tileFailed: "Niepowodzenia",
    tileXfailed: "Oczekiwane niepowodzenia",
    tilePassed: "Zaliczone",
    tileSkipped: "Nieuruchomione tutaj",
    tileSteps: "Kroki",
    tileShowAll: "pokaż znowu wszystko",
    tileShowOnly: "pokaż tylko: {{label}}",
    filterPlaceholder: "filtruj po nazwie",
    filterLabel: "filtruj scenariusze po nazwie",
    showing: "Pokazane {{shown}} z {{total}}.",
    showEverything: "pokaż wszystko",
    awaitingVerdicts:
      "Ławka nie przysłała jeszcze wyników tego biegu, więc to są nieudane <b>kontrole</b>, a nie wyniki testów. Szkicowy test, który nie przechodzi kontroli, jest oczekiwanym niepowodzeniem, a nie regresją.",
    lostVerdicts:
      "Ławka nigdy nie przysłała wyników tego biegu, więc to są nieudane <b>sprawdzenia</b>, a nie wyniki testów. Bieg najpewniej stanął, zanim testy zdążyły je zgłosić; log mówi, gdzie.",
    noFailuresMatch: "Nic z niepowodzeń nie pasuje do filtra.",
    nothingFailed: "W tym biegu nic nie zawiodło.",
    sectionFailed: "Niepowodzenia",
    sectionFailedN: "Niepowodzenia ({{count}})",
    sectionExpected: "Oczekiwane niepowodzenia — szkice ({{count}})",
    sectionPassed: "Zaliczone ({{count}})",
    sectionSkipped: "Pominięte ({{count}})",
    nothingMatches: "Nic nie pasuje. Liczby wyżej dotyczą całego biegu.",
    noCases: "Dla tego biegu nie zapisano żadnych przypadków.",
    tablistLabel: "co pokazać o tym biegu",
    tabFailures: "Niepowodzenia",
    tabAll: "Wszystkie przypadki",
    tabReport: "Raport",
    tabLog: "Log",
    deleteTitle: "Usuń {{name}}",
    deleteWarn:
      "Usuwa to bieg i wszystko, co z nim zapisano — kroki, werdykty, klatki i zbudowane raporty. Nie da się tego cofnąć. Wpisz <name>{{name}}</name>, żeby potwierdzić.",
    buildChangedSince: "wersja zmieniła się od <runLink>{{run}}</runLink>",
    harnessDigestTitle: "skrót wszystkich plików harnessu",
    harness: "harness {{digest}}",
    productDataTitle:
      "skrót scenariuszy, planu, wzorców i sidecarów produktu",
    productData: "dane {{product}} {{digest}}",
    productFallback: "produktu",
    movedHarness: "harness",
    movedProductData: "dane produktu",
    movedJoin: " i ",
    movedSince: "{{what}} zmieniło się od <runLink>{{run}}</runLink>",
    none: "Brak.",
    groupTests_one: "{{count}} test",
    groupTests_few: "{{count}} testy",
    groupTests_many: "{{count}} testów",
    groupTests_other: "{{count}} testów",
    tallySkipped_one: "{{count}} nieuruchomiony tutaj",
    tallySkipped_few: "{{count}} nieuruchomione tutaj",
    tallySkipped_many: "{{count}} nieuruchomionych tutaj",
    tallySkipped_other: "{{count}} nieuruchomionego tutaj",
    caseSteps_one: "{{count}} krok",
    caseSteps_few: "{{count}} kroki",
    caseSteps_many: "{{count}} kroków",
    caseSteps_other: "{{count}} kroków",
    caseFailedChecks_one: " · {{count}} nieudana kontrola",
    caseFailedChecks_few: " · {{count}} nieudane kontrole",
    caseFailedChecks_many: " · {{count}} nieudanych kontroli",
    caseFailedChecks_other: " · {{count}} nieudanych kontroli",
    shouldShow: "Powinno pokazać",
    definition: "definicja",
    noSteps: "Ten przypadek nie zapisał żadnego kroku; werdykt powyżej to wszystko, co zostawił.",
    browseCase: "Ten przypadek w innych biegach projektu",
    requires: "Wymaga",
    preconditions: "Warunki wstępne",
    expected: "Oczekiwane",
    returned: "Zwrócono",
    stdout: "stdout",
    stderr: "stderr",
    sent: "wysłano",
    truncated: "… ucięte",
    goldenRecordedFrom: "Wzorzec zapisany z",
    goldenElsewhere: "Wzorzec zapisany gdzie indziej: {{list}}.",
    goldenMissed: "Ekran różni się od wzorca: zgodność {{score}}, próg {{threshold}} (brakuje {{gap}}).",
    goldenMaskedMuch: "{{pct}}% ekranu jest zamaskowane i nie było porównywane.",
    stepGiven: "Co krok dostał",
    stepRaised: "Krok {{step}} · {{action}} przerwany błędem:",
    goldenElsewhereDisplayOs: "DisplayOS wtedy {{golden}}, teraz {{current}}",
    goldenElsewhereLayout: "układ wtedy {{golden}}, teraz {{current}}",
    goldenElsewhereLayoutFile: "inny plik układu",
    goldenElsewhereFonts: "inne czcionki",
    goldenElsewhereCapture: "zrzut wtedy przez {{golden}}, teraz przez {{current}}",
    goldenElsewhereOther: "{{key}} wtedy {{golden}}, teraz {{current}}",
    goldenElsewhereUnknown: "Nie dało się sprawdzić, czy wzorzec zapisano tutaj: {{detail}}",
    theMiss: "rozbieżność, której ten krok dowodzi",
    recorded: "zapisane",
    thresholdSource: "próg: {{source}}",
    masked: "maski: {{list}}",
    maskedFraction: " · {{pct}}% klatki",
    stillScreenFloor: "podłoga nieruchomego ekranu {{score}}",
    belowNoise:
      "Próg jest ciaśniejszy niż różnica między dwoma zdjęciami tego samego ekranu w chwili zapisu wzorca. To porównanie nie może przechodzić niezawodnie, cokolwiek robi produkt: zamaskuj to, co się rusza, albo poluzuj próg.",
    colRegion: "obszar",
    colScore: "wynik",
    colThreshold: "próg",
    notScored: "nie liczone na obrazie „{{label}}”: [{{rect}}]",
    frameFallback: "klatka",
    blink: "mrugaj",
    blinkTitle: "przełączaj między klatką a wzorcem",
    mask: "maska",
    maskTitle: "obrysuj obszary wyłączone z oceny",
    blinking: " · mruganie",
    maskedAreas_one: " · {{count}} zamaskowany obszar, nie liczony",
    maskedAreas_few: " · {{count}} zamaskowane obszary, nie liczone",
    maskedAreas_many: " · {{count}} zamaskowanych obszarów, nie liczonych",
    maskedAreas_other: " · {{count}} zamaskowanych obszarów, nie liczonych",
    frameAlt: "{{label}} z kroku {{step}}",
    frameLabel: { frame: "klatka", golden: "wzorzec", difference: "różnica" },
    scene: {
      verdict_one: "{{count}} różnica na {{compared}} elementów",
      verdict_few: "{{count}} różnice na {{compared}} elementów",
      verdict_many: "{{count}} różnic na {{compared}} elementów",
      verdict_other: "{{count}} różnicy na {{compared}} elementów",
      elements_one: "{{count}} element",
      elements_few: "{{count}} elementy",
      elements_many: "{{count}} elementów",
      elements_other: "{{count}} elementu",
      nothingCompared: "nic nie porównano",
      within: "w obrębie {{path}}",
      ignored: "pominięte {{list}}",
      ignoredFields: "pominięte pola: {{list}}",
      settled_one: "ustalone po {{count}} odczycie",
      settled_few: "ustalone po {{count}} odczytach",
      settled_many: "ustalone po {{count}} odczytach",
      settled_other: "ustalone po {{count}} odczytu",
      colPath: "ścieżka",
      colField: "pole",
      colGolden: "wzorzec",
      colNow: "teraz",
      absent: "brak",
      showAll_one: "pokaż wszystkie ({{count}})",
      showAll_few: "pokaż wszystkie ({{count}})",
      showAll_many: "pokaż wszystkie ({{count}})",
      showAll_other: "pokaż wszystkie ({{count}})",
      showFewer: "pokaż mniej",
      added: "Pojawiły się",
      removed: "Zniknęły",
      notListed_one: "{{count}} dalsza różnica nie mieści się tutaj; pełna lista leży przy biegu: {{detail}}",
      notListed_few: "{{count}} dalsze różnice nie mieszczą się tutaj; pełna lista leży przy biegu: {{detail}}",
      notListed_many: "{{count}} dalszych różnic nie mieści się tutaj; pełna lista leży przy biegu: {{detail}}",
      notListed_other: "{{count}} dalszej różnicy nie mieści się tutaj; pełna lista leży przy biegu: {{detail}}",
    },
    nowRunning: "teraz trwa",
    selfTestPassed: "Autotesty harnessu przeszły przed tym biegiem.",
    selfTestPassedWith:
      "Autotesty harnessu przeszły przed tym biegiem — {{summary}}.",
    selfTestFailed:
      "<b>Harness nie przeszedł własnych testów.</b> Cokolwiek ten bieg mówi o produkcie, zmierzył go harness, który sam się nie trzyma — czytaj poniższe liczby z tą świadomością albo wcale.",
    logSize: "{{kb}} kB, tak jak zapisał to bieg.",
    runLog: "Log biegu ({{kb}} kB)",
    since: "Porównanie z",
    compared: " (porównano {{count}})",
    nothingChanged: "Żaden test nie zmienił wyniku.",
    onlyThen_one:
      " {{count}} scenariusz z tamtego biegu nie wykonał się w tym.",
    onlyThen_few:
      " {{count}} scenariusze z tamtego biegu nie wykonały się w tym.",
    onlyThen_many:
      " {{count}} scenariuszy z tamtego biegu nie wykonało się w tym.",
    onlyThen_other:
      " {{count}} scenariuszy z tamtego biegu nie wykonało się w tym.",
    newlyFailing: "nowe niepowodzenia ({{count}})",
    newlyPassing: "nowe zaliczenia ({{count}})",
    reportHeading: "Raport",
    noTemplates: "Nie zdefiniowano szablonów raportu. <link>Dodaj jeden</link>.",
    reportNote:
      "Renderowane przez usługę z własnych zapisów tego biegu — działa, gdy ławka jest zajęta, offline albo już jej nie ma. Zawartość zmieniasz w dziale <link>Raporty</link>.",
    asPdf: "{{name}} jako PDF",
    archive_one: "Archiwum: {{count}} PDF zbudowany na ławce w trakcie biegu",
    archive_few: "Archiwum: {{count}} PDF-y zbudowane na ławce w trakcie biegu",
    archive_many: "Archiwum: {{count}} PDF-ów zbudowanych na ławce w trakcie biegu",
    archive_other: "Archiwum: {{count}} PDF-ów zbudowanych na ławce w trakcie biegu",
    pdfHelp: {
      summary: "tylko werdykty i przegląd — bez szczegółów per przypadek",
      results: "każdy przypadek z werdyktem i uzasadnieniem — bez krok po kroku",
      failures: "niepowodzenia w całości, nic więcej",
      standard: "każdy przypadek, krok po kroku, bez zrzutów",
      full: "każdy przypadek ze zrzutami, które zrobiła ławka",
    },
    phaseCleanup: "Sprzątanie",
    phaseSetup: "Przed pierwszym krokiem",
    phasePassed: "zaliczone",
    phasePassedRestored: "zaliczone — przywrócono {{count}}",
    phaseFailed: "niepowodzenie",
    saved: "Zapisano",
    runningEllipsis: "trwa…",
    noAssertion: "brak asercji — tylko przygotowanie",
    fail: "FAIL",
    pass: "PASS",
    mb: "{{n}} MB",
    kb: "{{n}} kB",
  },
  human: {
    step: "Krok {{n}}",
    timeout_one: "Po {{secs}} s i {{count}} próbie:",
    timeout_few: "Po {{secs}} s i {{count}} próbach:",
    timeout_many: "Po {{secs}} s i {{count}} próbach:",
    timeout_other: "Po {{secs}} s i {{count}} próbach:",
    rc: "Kod wyjścia {{rc}} zamiast {{want}}",
    rcNested: "kod wyjścia {{rc}} zamiast {{want}}",
    noMessage: "bez komunikatu błędu",
    expected: "oczekiwano",
    unchanged: "bez zmian, a miało się zmienić",
    notContains: "nie zawiera",
    noMatch: "nie pasuje do",
    outOfRange: "poza zakresem {{lo}}–{{hi}}",
    missing: "brak w odpowiedzi",
    stdoutLacks: "Wyjście nie zawiera",
    stdoutHas: "Niechciane w wyjściu:",
    stdoutNe: "Wyjście to",
    stdoutSame: "Wyjście się nie zmieniło:",
    httpTimeout: "Brak odpowiedzi HTTP z {{host}}:{{port}} w ciągu {{secs}} s",
    httpDropped: "Połączenie zerwane bez odpowiedzi",
    notJson: "Odpowiedź nie była JSON-em",
    cmdTimeout: "Polecenie nie skończyło się w {{secs}} s",
    cmdFailed: "Polecenie zakończyło się kodem {{rc}}",
    truncated: "(harness uciął resztę)",
    raw: "Surowy komunikat",
    moreLines_one: "(+{{count}} linia)",
    moreLines_few: "(+{{count}} linie)",
    moreLines_many: "(+{{count}} linii)",
    moreLines_other: "(+{{count}} linii)",
    until: "aż {{what}}",
    and: "i",
    within: "do {{secs}} s",
    every: "co {{secs}} s",
    seconds: "{{v}} s",
    output: "wyjście",
    a: {
      rc: "kod {{v}}",
      status: "HTTP {{v}}",
      truthy: "{{p}} niepuste",
      falsy: "{{p}} puste",
      exists: "{{p}} jest",
      absent: "brak {{p}}",
      contains: "{{p}} zawiera {{v}}",
      stdoutContains: "wyjście zawiera {{v}}",
      stdoutLacks: "wyjście nie zawiera {{v}}",
      stdoutNotBlank: "wyjście niepuste",
      notBlank: "obraz niepusty",
      frameDiffers: "obraz inny niż {{v}}",
      size: "obraz {{w}}×{{h}}",
    },
  },
};

export default pl;
